import React from 'react';
import {
  AlertTriangle,
  Calendar,
  BookOpen,
  Check,
  Circle,
  Clock,
  Download,
  Lock,
  Loader2,
  Play,
  TrendingUp,
  User,
} from 'lucide-react';

import { ReportsTrendsChartContent } from 'components/mentorship/admin/ReportsTrendsCard';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Progress } from 'components/ui/progress';
import type { LifeScoresReport, CourseEligibility } from 'types';

type CourseStatus = 'not_started' | 'in_progress' | 'completed';
type FlowState = 'completed' | 'in_progress' | 'locked' | 'not_started';

interface FlowDetail {
  id: string;
  name: string;
  chapterId?: number | null;
  chapterTitle?: string | null;
  currentState: FlowState;
  completed: boolean;
  started: boolean;
  locked: boolean;
  progressPercent?: number;
  completedAt?: string | null;
  lastAccessedAt?: string | null;
}

interface AssignedCourse {
  id: string;
  name: string;
  status: CourseStatus;
  eligibility?: CourseEligibility;
  progressPercent: number;
  flows: FlowDetail[];
  completedAt?: string | null;
  lastAccessedAt?: string | null;
}

interface UserDetailShape {
  displayName: string;
  email: string;
  avatarUrl?: string | null;
  dailyScoresReport: LifeScoresReport | null;
  assignedCourses: AssignedCourse[];
  standaloneFlows: FlowDetail[];
}

interface UserDetailSectionProps {
  userDetail?: UserDetailShape | null;
  isLoading: boolean;
  isError: boolean;
  onExportDetail: () => void;
  isExportingDetail: boolean;
  onExportDailyScores: () => void;
  isExportingDailyScores: boolean;
}

const formatDetailDate = (value?: string | null) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const DateMeta: React.FC<{ label: 'Completed' | 'Last accessed'; value?: string | null; className?: string }> = ({
  label,
  value,
  className,
}) => {
  const formatted = formatDetailDate(value);
  if (!formatted) {
    return null;
  }

  return (
    <span className={className ?? 'flex items-center gap-1 text-xs text-muted-foreground'}>
      <Calendar className="size-3" />
      {label} {formatted}
    </span>
  );
};

const CourseStatusBadge: React.FC<{ status: CourseStatus }> = ({ status }) => {
  if (status === 'not_started') {
    return (
      <Badge variant="outline" className="border-muted text-xs text-muted-foreground">
        <Circle className="mr-1 size-3" />
        Not started
      </Badge>
    );
  }
  if (status === 'in_progress') {
    return (
      <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 text-xs">
        <Clock className="mr-1 size-3" />
        In progress
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700 text-xs">
      <Check className="mr-1 size-3" />
      Completed
    </Badge>
  );
};

const FlowStateIcon: React.FC<{ currentState: FlowState }> = ({ currentState }) => {
  if (currentState === 'completed') {
    return <Check className="size-4 text-green-700" />;
  }
  if (currentState === 'in_progress') {
    return <Clock className="size-4 text-amber-700" />;
  }
  if (currentState === 'locked') {
    return <Lock className="size-4 text-muted-foreground" />;
  }
  return <Circle className="size-4 text-muted-foreground" />;
};

const FlowItem: React.FC<{ flow: FlowDetail }> = ({ flow }) => (
  <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center rounded-md bg-muted/30 px-3 py-2 transition-colors hover:bg-muted/50">
    <div className="flex items-center gap-3">
      <FlowStateIcon currentState={flow.currentState} />
      <span className="text-sm text-foreground">{flow.name}</span>
    </div>
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      {flow.completed ? <DateMeta label="Completed" value={flow.completedAt} /> : null}
      {!flow.completed && flow.currentState === 'in_progress' ? (
        <DateMeta label="Last accessed" value={flow.lastAccessedAt} />
      ) : null}
      {flow.completed ? <span className="text-green-700">Completed</span> : null}
      {flow.started && !flow.completed ? <span>Started</span> : null}
      {flow.locked ? (
        <span className="flex items-center gap-1">
          <Lock className="size-3" />
          Locked
        </span>
      ) : null}
    </div>
  </div>
);

const StandaloneFlowItem: React.FC<{ flow: FlowDetail }> = ({ flow }) => (
  <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-center rounded-md bg-white px-4 py-3 shadow-sm transition-colors hover:bg-accent/30">
    <div className="flex items-center gap-3">
      <FlowStateIcon currentState={flow.currentState} />
      <span className="text-sm font-medium text-foreground">{flow.name}</span>
    </div>
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-2">
        <Progress value={flow.progressPercent || 0} className="h-1.5 w-16" />
        <span className="w-8 text-xs text-muted-foreground">{Math.round(flow.progressPercent || 0)}%</span>
      </div>
      <div className="flex items-center gap-3 text-xs">
        {flow.completed ? <DateMeta label="Completed" value={flow.completedAt} /> : null}
        {!flow.completed && flow.currentState === 'in_progress' ? (
          <DateMeta label="Last accessed" value={flow.lastAccessedAt} />
        ) : null}
        {flow.completed ? (
          <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700 text-xs">
            Completed
          </Badge>
        ) : flow.started ? (
          <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 text-xs">
            Started
          </Badge>
        ) : null}
      </div>
    </div>
  </div>
);

export const UserDetailSection: React.FC<UserDetailSectionProps> = ({
  userDetail,
  isLoading,
  isError,
  onExportDetail,
  isExportingDetail,
  onExportDailyScores,
  isExportingDailyScores,
}) => {
  if (!isLoading && !isError && !userDetail) {
    return (
      <section aria-labelledby="user-detail-heading" className="space-y-4">
        <h2 id="user-detail-heading" className="text-xl font-semibold text-foreground sm:text-2xl">
          User Detail
        </h2>
        <Card disableAnimation maxWidth="none" className="bg-white p-0 shadow-elevation-sm">
          <CardContent variant="custom" className="px-6 py-12 text-center">
            <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
              <User className="size-5" />
            </div>
            <h3 className="text-base font-semibold text-foreground">No user selected</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Select a user from the table above to view their detailed progress.
            </p>
          </CardContent>
        </Card>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section aria-labelledby="user-detail-heading" className="space-y-4">
        <h2 id="user-detail-heading" className="text-xl font-semibold text-foreground sm:text-2xl">
          User Detail
        </h2>
        <Card disableAnimation maxWidth="none" className="bg-white p-0 shadow-elevation-sm">
          <CardContent variant="custom" className="space-y-6 p-6">
            <div className="flex items-center gap-4">
              <div className="size-12 animate-pulse rounded-full bg-muted" />
              <div className="space-y-2">
                <div className="h-5 w-40 animate-pulse rounded bg-muted" />
                <div className="h-4 w-52 animate-pulse rounded bg-muted" />
              </div>
            </div>
            <div className="space-y-3">
              <div className="h-5 w-32 animate-pulse rounded bg-muted" />
              <div className="h-24 w-full animate-pulse rounded-lg bg-muted" />
              <div className="h-24 w-full animate-pulse rounded-lg bg-muted" />
            </div>
          </CardContent>
        </Card>
      </section>
    );
  }

  if (isError) {
    return (
      <section aria-labelledby="user-detail-heading" className="space-y-4">
        <h2 id="user-detail-heading" className="text-xl font-semibold text-foreground sm:text-2xl">
          User Detail
        </h2>
        <Card disableAnimation maxWidth="none" className="bg-white p-0 shadow-elevation-sm">
          <CardContent variant="custom" className="px-6 py-12 text-center">
            <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <AlertTriangle className="size-5" />
            </div>
            <h3 className="text-base font-semibold text-foreground">Error loading user details</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Unable to load user details. Please try again later.
            </p>
          </CardContent>
        </Card>
      </section>
    );
  }

  const accordionDefault = userDetail?.assignedCourses.map((course) => course.id) || [];

  return (
    <section aria-labelledby="user-detail-heading" className="space-y-4">
      <h2 id="user-detail-heading" className="text-xl font-semibold text-foreground sm:text-2xl">
        User Detail
      </h2>
      <Card disableAnimation maxWidth="none" className="bg-white p-0 shadow-elevation-sm">
        <CardContent variant="custom" className="space-y-6 p-6">
          <div className="flex flex-col gap-4 border-b border-border pb-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <Avatar
                data-testid="org-admin-user-avatar"
                data-avatar-url={userDetail?.avatarUrl ?? undefined}
                className="size-12 shrink-0 bg-primary/10 text-primary"
              >
                {userDetail?.avatarUrl ? (
                  <AvatarImage src={userDetail.avatarUrl} alt="Profile photo" className="object-cover" />
                ) : null}
                <AvatarFallback className="bg-primary/10 text-primary">
                  <User className="size-6" />
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold text-foreground">{userDetail?.displayName}</p>
                <p className="text-sm text-muted-foreground">{userDetail?.email}</p>
              </div>
            </div>
            <Button
              type="button"
              size="auto"
              data-testid="org-admin-export-detail-progress"
              onClick={onExportDetail}
              disabled={isExportingDetail}
            >
              {isExportingDetail ? <Loader2 className="animate-spin" /> : <Download />}
              {isExportingDetail ? 'Exporting...' : 'Export User Detail Report'}
            </Button>
          </div>

          <div>
            <h3 className="mb-4 flex items-center gap-2 text-2xl font-semibold text-foreground">
              <BookOpen className="size-5" />
              Assigned Courses
            </h3>
            {userDetail?.assignedCourses.length ? (
              <Accordion type="multiple" defaultValue={accordionDefault} className="space-y-3">
                {userDetail.assignedCourses.map((course) => (
                  <AccordionItem key={course.id} value={course.id} className="bg-white shadow-sm">
                    <AccordionTrigger className="w-full px-4 py-4 hover:no-underline">
                      <div className="flex w-full flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                        <div className="space-y-2 text-left">
                          <div className="text-base font-medium text-foreground">{course.name}</div>
                          <Progress value={course.progressPercent} className="h-1.5 w-52 max-w-full" />
                          {course.status === 'completed' ? (
                            <DateMeta label="Completed" value={course.completedAt} />
                          ) : null}
                          {course.status === 'in_progress' ? (
                            <DateMeta label="Last accessed" value={course.lastAccessedAt} />
                          ) : null}
                        </div>
                        <div className="flex items-center gap-3">
                          <CourseStatusBadge status={course.status} />
                          {course.eligibility?.is_locked && <Badge variant="outline" className="border-amber-200 bg-amber-50 text-xs text-amber-800"><Lock className="mr-1 h-3 w-3" />Locked</Badge>}
                          <span className="text-sm font-medium text-foreground">{Math.round(course.progressPercent)}%</span>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-2 px-4 pb-4">
                      {course.eligibility?.is_locked && <div className="mb-3 space-y-1 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"><p className="font-medium">Prerequisites incomplete · progress preserved</p><ul className="list-disc pl-4">{course.eligibility.unmet_prerequisites.map(prerequisite => <li key={prerequisite.id}>{prerequisite.name} · {prerequisite.is_assigned ? `${Math.round(prerequisite.progress_percent)}% complete` : 'Not assigned to this learner'}</li>)}</ul></div>}
                      <p className="mb-3 text-xs text-muted-foreground">
                        {course.flows.length} module{course.flows.length !== 1 ? 's' : ''} in this course
                      </p>
                      <div className="space-y-1.5">
                        {course.flows.map((flow, index) => (
                          <React.Fragment key={flow.id}>
                            {flow.chapterId && course.flows[index-1]?.chapterId !== flow.chapterId ? <h4 className="pt-4 pb-2 text-sm font-semibold">{flow.chapterTitle || 'Chapter'}</h4> : null}
                            {!flow.chapterId && course.flows[index-1]?.chapterId ? <p className="pt-4 pb-2 text-xs text-muted-foreground">Ungrouped modules</p> : null}
                            <FlowItem flow={flow} />
                          </React.Fragment>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <div className="rounded-lg border border-dashed border-border px-6 py-10 text-center">
                <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <BookOpen className="size-5" />
                </div>
                <h4 className="text-base font-semibold text-foreground">No assigned courses</h4>
                <p className="mt-2 text-sm text-muted-foreground">
                  This user has no active assigned courses.
                </p>
              </div>
            )}
          </div>

          <div>
            <h3 className="mb-4 flex items-center gap-2 text-2xl font-semibold text-foreground">
              <Play className="size-5" />
              Standalone Assigned Modules
            </h3>
            {userDetail?.standaloneFlows.length ? (
              <div className="space-y-2">
                {userDetail.standaloneFlows.map((flow) => (
                  <StandaloneFlowItem key={flow.id} flow={flow} />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border px-6 py-10 text-center">
                <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Play className="size-5" />
                </div>
                <h4 className="text-base font-semibold text-foreground">No standalone assigned modules</h4>
                <p className="mt-2 text-sm text-muted-foreground">
                  This user has no standalone modules assigned outside of courses.
                </p>
              </div>
            )}
          </div>

          <div>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="flex items-center gap-2 text-2xl font-semibold text-foreground">
                <TrendingUp className="size-5" />
                Daily Scores
              </h3>
              <Button
                type="button"
                size="auto"
                data-testid="org-admin-export-daily-scores"
                onClick={onExportDailyScores}
                disabled={isExportingDailyScores}
              >
                {isExportingDailyScores ? <Loader2 className="animate-spin" /> : <Download />}
                {isExportingDailyScores ? 'Exporting...' : 'Export Daily Scores'}
              </Button>
            </div>
            <ReportsTrendsChartContent
              data={userDetail?.dailyScoresReport}
              selectedUserId="selected-user"
              emptyStateText="No daily scores data available yet."
            />
          </div>
        </CardContent>
      </Card>
    </section>
  );
};
